import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { useTranslation } from "react-i18next";
import { RefreshCw } from "lucide-react";
import {
  Avatar,
  Badge,
  Button,
  DataTable,
  DateRangeInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SettingsSection,
  TableIdentity,
  updateTableState,
  type DataTableChange,
  type DataTableColumn,
  type DataTableState,
} from "@h2j-data-team/ui";

import { DensityToggle } from "@/components/density-toggle";
import { useFormatDate } from "@/hooks/use-format-date";
import { useLocale } from "@/hooks/use-locale";
import { listRefreshing, paginationLabels, tableMessages } from "@/lib/ui-labels";
import { useTableDensity } from "@/lib/use-table-density";
import { auditLogsPageQueryOptions, type AuditLogEntry } from "@/features/audit-logs/api";

export const Route = createFileRoute("/_authenticated/settings/audit")({
  staticData: { pageTitle: "breadcrumbs.activityLog" },
  component: AuditSection,
});

const ACTION_BADGE_VARIANTS: Record<string, "success" | "info" | "danger"> = {
  create: "success",
  update: "info",
  delete: "danger",
};

const ACTION_OPTIONS = ["create", "update", "delete"];

const ENTITY_TYPE_OPTIONS = [
  "variable",
  "qlik_connection",
  "group",
  "group_app",
  "group_variable",
  "group_dimension",
  "group_measure",
  "dimension",
  "measure",
];

const ALL_VALUE = "__all__";

/** The table's filters: absent means "not filtering" (never an empty sentinel). Dates are YYYY-MM-DD. */
type AuditTableFilters = { action?: string; entityType?: string; from?: string; to?: string };

function AuditSection() {
  const { t } = useTranslation("dashboard");
  const { t: tOrg } = useTranslation("organizations");
  const { t: tc } = useTranslation("common");
  const { dateFnsLocale } = useLocale();
  const { formatDateTime } = useFormatDate();
  const { density, setDensity } = useTableDensity();

  const [state, setState] = useState<DataTableState<AuditTableFilters>>({
    page: 1,
    pageSize: 25,
    query: "",
    filters: {},
    selection: [],
  });
  // cursors[0] is the start; cursors[p] opens page p + 1, saved when moving forward
  // from page p. Any filter or size change returns to page 1, which is cursors[0].
  const [cursors, setCursors] = useState<(string | null)[]>([null]);
  const cursor = cursors[state.page - 1] ?? null;
  const { filters } = state;

  const query = useQuery(
    auditLogsPageQueryOptions(
      {
        action: filters.action,
        entityType: filters.entityType,
        dateFrom: filters.from,
        dateTo: filters.to,
      },
      state.pageSize,
      cursor,
    ),
  );

  const entries = query.data?.data ?? [];
  // While another page loads, the rows (and their nextCursor) still belong to the previous one.
  const hasNext = !query.isPlaceholderData && !!query.data?.nextCursor;

  function onStateChange(next: DataTableState<AuditTableFilters>, change: DataTableChange) {
    if (change.reason === "user" && next.page === state.page + 1 && query.data?.nextCursor) {
      const saved = query.data.nextCursor;
      setCursors((stack) => [...stack.slice(0, state.page), saved]);
    }
    setState(next);
  }

  const patch = (p: Partial<AuditTableFilters>) =>
    onStateChange(updateTableState(state, { filters: { ...filters, ...p } }), { reason: "user" });
  const clearFilters = () =>
    onStateChange(updateTableState(state, { filters: {} }), { reason: "user" });
  const hasActiveFilters = !!(filters.action || filters.entityType || filters.from || filters.to);

  const columns: DataTableColumn<AuditLogEntry>[] = [
    {
      id: "when",
      header: t("activity.columns.when"),
      width: "10rem",
      rowHeader: true,
      cell: (entry) => (
        <time dateTime={entry.createdAt} title={formatDateTime(entry.createdAt)}>
          {formatDistanceToNow(new Date(entry.createdAt), {
            addSuffix: true,
            locale: dateFnsLocale,
          })}
        </time>
      ),
    },
    {
      id: "who",
      header: t("activity.columns.who"),
      width: "12rem",
      variant: "identity",
      cell: (entry) => {
        const name = entry.actor
          ? entry.actor.name === entry.actor.email
            ? entry.actor.email
            : entry.actor.name
          : t("activity.actor.system");
        return (
          <TableIdentity
            leading={entry.actor ? <Avatar size="sm" name={name} /> : undefined}
            // The identity cell is one line with an ellipsis: the full text goes in the tooltip.
            name={<span title={name}>{name}</span>}
          />
        );
      },
    },
    {
      id: "action",
      header: t("activity.columns.action"),
      width: "7rem",
      cell: (entry) => (
        <Badge variant={ACTION_BADGE_VARIANTS[entry.action] ?? "outline"}>
          {t(`activity.actions.${entry.action}`, { defaultValue: entry.action })}
        </Badge>
      ),
    },
    {
      id: "entity",
      header: t("activity.columns.entity"),
      variant: "identity",
      cell: (entry) => (
        <TableIdentity
          name={<span title={entry.payload.summary}>{entry.payload.summary}</span>}
          detail={t(`activity.entityTypes.${entry.entityType}`, {
            defaultValue: entry.entityType,
          })}
          detailMono={false}
        />
      ),
    },
    {
      id: "changed",
      header: t("activity.columns.changed"),
      width: "10rem",
      cell: (entry) => {
        const changedFields = Object.keys(entry.payload.changes ?? {});
        return (
          <span className="font-mono text-[11.5px] text-faint">
            {changedFields.length > 0 ? changedFields.join(", ") : "—"}
          </span>
        );
      },
    },
  ];

  return (
    <SettingsSection title={t("activity.pageTitle")} description={t("activity.description")}>
      <DataTable
        label={t("activity.pageTitle")}
        bordered={false}
        density={density}
        minWidth="44rem"
        columns={columns}
        rows={entries}
        getRowId={(entry) => entry.id}
        state={state}
        onStateChange={onStateChange}
        loading={query.isPending}
        refreshing={listRefreshing(query)}
        error={query.isError ? { title: tc("error.loadFailed") } : null}
        onRetry={() => void query.refetch()}
        pagination={{ kind: "cursor", hasNext, labels: paginationLabels() }}
        toolbar={
          <>
            <Select
              value={filters.action ?? ALL_VALUE}
              onValueChange={(value) => patch({ action: value === ALL_VALUE ? undefined : value })}
            >
              <SelectTrigger
                size="compact"
                className="w-44"
                aria-label={t("activity.filters.action")}
              >
                <SelectValue placeholder={t("activity.filters.allActions")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_VALUE}>{t("activity.filters.allActions")}</SelectItem>
                {ACTION_OPTIONS.map((value) => (
                  <SelectItem key={value} value={value}>
                    {t(`activity.actions.${value}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={filters.entityType ?? ALL_VALUE}
              onValueChange={(value) =>
                patch({ entityType: value === ALL_VALUE ? undefined : value })
              }
            >
              <SelectTrigger
                size="compact"
                className="w-40"
                aria-label={t("activity.filters.entityType")}
              >
                <SelectValue placeholder={t("activity.filters.allTypes")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_VALUE}>{t("activity.filters.allTypes")}</SelectItem>
                {ENTITY_TYPE_OPTIONS.map((value) => (
                  <SelectItem key={value} value={value}>
                    {t(`activity.entityTypes.${value}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <DateRangeInput
              size="compact"
              aria-label={tOrg("audit.filters.period")}
              value={{ from: filters.from, to: filters.to }}
              onValueChange={({ from, to }) => patch({ from, to })}
              fromLabel={t("activity.filters.from")}
              toLabel={t("activity.filters.to")}
            />
            {hasActiveFilters && (
              <Button variant="ghost" size="compact" onClick={clearFilters}>
                {t("activity.filters.clearFilters")}
              </Button>
            )}
            <span className="ms-auto flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon-compact"
                aria-label={t("activity.refresh")}
                title={t("activity.refresh")}
                onClick={() => void query.refetch()}
                disabled={query.isFetching}
              >
                <RefreshCw className={query.isFetching ? "animate-spin" : undefined} />
              </Button>
              <DensityToggle value={density} onChange={setDensity} />
            </span>
          </>
        }
        messages={tableMessages({
          empty: {
            title: t("activity.empty.title"),
            description: t("activity.empty.description"),
          },
          noResults: {
            title: t("activity.empty.title"),
            description: t("activity.empty.filteredDescription"),
            action: (
              <Button variant="outline" size="sm" onClick={clearFilters}>
                {t("activity.filters.clearFilters")}
              </Button>
            ),
          },
        })}
      />
    </SettingsSection>
  );
}
